import React, { useState, useEffect } from 'react';
import './App.css';
import Dashboard from './pages/Dashboard';
import Campaigns from './pages/Campaigns';
import Leads from './pages/Leads';
import VoiceLibrary from './pages/VoiceLibrary';
import Analytics from './pages/Analytics';
import CallHistory from './pages/CallHistory';
import InboundCalls from './pages/InboundCalls';
import Tickets from './pages/Tickets';
import Settings from './pages/Settings';
import Agents from './pages/Agents';
import Admin from './pages/Admin';
import Inventory from './pages/Inventory';
import Calendar from './pages/Calendar';
import Reports from './pages/Reports';
import Tasks from './pages/Tasks';
import SmsAutomation from './pages/SmsAutomation';
import Login from './pages/Login';
import Sidebar from './components/Sidebar';
import { Menu, Moon, Sun, LogOut } from 'lucide-react';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { navItemsForRole, isPageAllowed } from './auth/permissions';
import { AmbientBackground } from './components/ui/ambient-background';
import { AIBubble } from './components/ui/ai-bubble';

function LiveClock() {
  const [now, setNow] = React.useState(new Date());
  React.useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const text = now.toLocaleTimeString([], {
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
  return (
    <div className="hidden md:flex items-center gap-2 px-3 py-1 rounded-full border border-ink-200/70 dark:border-ink-800/70 bg-white/40 dark:bg-ink-900/40">
      <span className="pulse-dot" aria-hidden="true" />
      <span className="text-[11px] text-ink-500">
        Live <span className="text-ink-300">·</span>{' '}
        <span className="nums text-ink-700 dark:text-ink-200">{text}</span>
      </span>
    </div>
  );
}

function AppShell() {
  const { user, isAuthenticated, isChecking, logout } = useAuth();
  const [currentPage, setCurrentPage] = useState('dashboard');
  // Default to LIGHT mode now (prior versions defaulted to dark)
  const [isDark, setIsDark] = useState(() => {
    try {
      const saved = localStorage.getItem('theme');
      if (saved === 'dark') return true;
      if (saved === 'light') return false;
    } catch (_) {}
    return false;
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('sidebar-collapsed') === '1';
    } catch (_) { return false; }
  });

  useEffect(() => {
    if (isDark) document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
    try { localStorage.setItem('theme', isDark ? 'dark' : 'light'); } catch (_) {}
  }, [isDark]);

  useEffect(() => {
    try { localStorage.setItem('sidebar-collapsed', sidebarCollapsed ? '1' : '0'); } catch (_) {}
  }, [sidebarCollapsed]);

  useEffect(() => {
    if (user && !isPageAllowed(currentPage, user.role)) {
      setCurrentPage('dashboard');
    }
  }, [user, currentPage]);

  if (isChecking) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-ink-500">Loading…</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) return <Login />;

  const renderPage = () => {
    if (!isPageAllowed(currentPage, user.role)) return <Dashboard />;
    switch (currentPage) {
      case 'dashboard': return <Dashboard />;
      case 'campaigns': return <Campaigns />;
      case 'leads': return <Leads />;
      case 'calendar': return <Calendar />;
      case 'inbound-calls': return <InboundCalls />;
      case 'tickets': return <Tickets />;
      case 'agents': return <Agents />;
      case 'voice-library': return <VoiceLibrary />;
      case 'call-history': return <CallHistory />;
      case 'analytics': return <Analytics />;
      case 'settings': return <Settings />;
      case 'admin': return <Admin />;
      case 'inventory': return <Inventory />;
      case 'reports': return <Reports />;
      case 'tasks': return <Tasks />;
      case 'sms-automation': return <SmsAutomation />;
      default: return <Dashboard />;
    }
  };

  const navItems = navItemsForRole(user.role);
  const displayName = [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email;
  const roleLabel = user.role.replace('_', ' ');
  const initials = ((user.first_name?.[0] || '') + (user.last_name?.[0] || '')).toUpperCase() || (user.email?.[0] || '?').toUpperCase();

  return (
    <div className="relative h-screen overflow-hidden bg-ink-50 dark:bg-ink-950 text-ink-900 dark:text-ink-50">
      <AmbientBackground />

      <div className="relative z-10 flex h-screen">
        <Sidebar
          items={navItems}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed((v) => !v)}
        />

        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          {/* Top bar — minimal: brand left, status + theme + avatar right */}
          <header className="h-14 border-b border-ink-200/70 dark:border-ink-800/70 bg-white/70 dark:bg-ink-950/60 backdrop-blur-md flex items-center justify-between gap-3 px-4 lg:px-5 flex-shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden w-9 h-9 rounded-md flex items-center justify-center text-ink-700 hover:bg-ink-100 dark:text-ink-100 dark:hover:bg-ink-900/60 -ml-2"
                data-testid="menu-toggle"
                aria-label="Open menu"
              >
                <Menu className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-7 h-7 rounded-lg bg-ink-900 dark:bg-white flex items-center justify-center flex-shrink-0">
                  <span className="text-[11px] font-bold text-white dark:text-ink-900 tracking-tighter">AM</span>
                </div>
                <div className="leading-tight hidden sm:block min-w-0">
                  <div className="text-sm font-semibold tracking-tight font-display truncate">AutoMind</div>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-ink-500">Dealership AI</div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              <LiveClock />
              {/* Phase Audit Fix C: NotificationCenter removed — backend
                  feed not wired; bell always showed 0 unread. Re-mount
                  once a real notifications service ships. */}
              <button
                onClick={() => setIsDark(!isDark)}
                className="w-9 h-9 rounded-md border border-ink-200/70 dark:border-ink-800/70 bg-white/40 dark:bg-ink-900/40 flex items-center justify-center text-ink-700 dark:text-ink-200 hover:bg-ink-100 dark:hover:bg-ink-800/60 transition-colors focus-2050"
                data-testid="theme-toggle"
                title={isDark ? 'Light mode' : 'Dark mode'}
                aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
              <div
                className="hidden md:flex items-center gap-2 pr-1 pl-2 py-1 rounded-md hover:bg-ink-100 dark:hover:bg-ink-800/60 transition-colors cursor-default"
                title={displayName}
                data-testid="user-badge"
              >
                <div className="leading-tight text-right">
                  <div className="text-xs font-medium text-ink-900 dark:text-white truncate max-w-[140px]">{displayName}</div>
                  <div className="text-[10px] text-ink-500 capitalize">{roleLabel}</div>
                </div>
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-ink-700 to-ink-900 dark:from-ink-200 dark:to-white flex items-center justify-center text-[11px] font-semibold text-white dark:text-ink-900">
                  {initials}
                </div>
              </div>
              <button
                onClick={logout}
                className="w-9 h-9 rounded-md text-ink-500 hover:bg-ink-100 dark:hover:bg-ink-800/60 flex items-center justify-center transition-colors focus-2050"
                data-testid="logout-button"
                title="Logout"
                aria-label="Logout"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </header>

          {/* Page surface */}
          <main className="flex-1 overflow-y-auto px-4 lg:px-5 py-5">
            <div key={currentPage} className="screen-in">
              {renderPage()}
            </div>
          </main>
        </div>
      </div>

      <AIBubble
        label="AutoMind AI"
        detail="ready"
        onClick={() => {/* future phase: open chat */}}
      />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  );
}
